import {
  useDeleteSessionMutation,
  useSetSessionBookmarkMutation,
  useUpdateSessionMutation,
} from '@/lib/features/session/sessionApi'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { toast } from 'react-toastify'

export function useThreadOperations() {
  const [deleteSession] = useDeleteSessionMutation()
  const [setSessionBookmark] = useSetSessionBookmarkMutation()
  const [updateSession] = useUpdateSessionMutation()
  const pathname = usePathname()
  const router = useRouter()

  const handleToggleBookmark = useCallback(
    async (sessionId: string, isBookmarked: boolean) => {
      try {
        await setSessionBookmark({
          sessionId,
          bookmarked: !isBookmarked,
        }).unwrap()
        toast.success(isBookmarked ? 'Bookmark removed' : 'Bookmark added')
      } catch (error) {
        console.error('Failed to toggle bookmark:', error)
        toast.error('Failed to update bookmark')
      }
    },
    [setSessionBookmark]
  )

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      const isCurrentThread = pathname.includes(sessionId)

      try {
        await deleteSession(sessionId).unwrap()
        toast.success('Thread deleted successfully')

        if (isCurrentThread) {
          router.push('/copilot')
        }
      } catch (error) {
        console.error('Failed to delete session:', error)
        toast.error('Failed to delete thread')
      }
    },
    [deleteSession, pathname, router]
  )

  const handleRenameSession = useCallback(
    async (sessionId: string, newName: string) => {
      if (!newName.trim()) {
        toast.error('Thread name cannot be empty')
        return false
      }

      try {
        await updateSession({ sessionId, name: newName }).unwrap()
        toast.success('Thread renamed successfully')
        return true
      } catch (error) {
        console.error('Failed to rename session:', error)
        toast.error('Failed to rename thread')
        return false
      }
    },
    [updateSession]
  )

  return {
    handleToggleBookmark,
    handleDeleteSession,
    handleRenameSession,
  }
}
